/**
 * Collection Vue Component
 * ------------------------------------------------------------------------------
 * Template in sections/collection.liquid
 *
 */

import Vue from 'vue';
import debounce from 'lodash-es/debounce';
import VueSlider from 'vue-slider-component';
import algoliasearch from 'algoliasearch/lite';

import ProductItem from './product-item.js';
import Tabs from './tabs.vue';
import Tab from './tab.vue';
import Accordion from './accordion.vue';
import Collapsible from './collapsible.vue';

import { app } from '../app.js';
import { fittingBox } from '../mixins/fittingBox.js';

import { getAlgoliaIndex } from '../utils/api';
import { updateUrlParameter, removeParameterFromUrl, getProductDetails, getSiblingsGraphQL } from './../utils/helpers';
import { cloneDeep, isEmpty } from 'lodash-es';

const DEFAULT_PAGINATION = 15;
const PRICE_KEY = 'price_range';
const SHOPIFY_SORT_OPTIONS = [
      {
        shopify_key: "manual",
        sort_by: "manual",
        dir: "asc"
      },
      {
        shopify_key: "price-ascending",
        sort_by: "price",
        dir: "asc"
      },
      {
        shopify_key: "price-descending",
        sort_by: "price",
        dir: "desc"
      },
      {
        shopify_key: "title-ascending",
        sort_by: "title",
        dir: "asc"
      },
      {
        shopify_key: "title-descending",
        sort_by: "title",
        dir: "desc"
      },
      {
        shopify_key: "created-ascending",
        sort_by: "published_at",
        dir: "asc"
      },
      {
        shopify_key: "created-descending",
        sort_by: "published_at",
        dir: "desc"
      },
      {
        shopify_key: "best-selling",
        sort_by: "recently_ordered_count",
        dir: "desc"
      }
  ];

export default {
    mixins: [
        fittingBox
    ],

    components: {
        ProductItem,
        Tabs,
        Tab,
        Accordion,
        Collapsible,
        VueSlider
    },

    props: {
        collectionHandle: {
            type: String,
            default: 'all'
        },
        defaultSort: {
            type: String
        },
        sortOptionLabels: {
            type: Object,
            required: true
        },
        filterConfig: {
            type: Array,
            required: true
        },
        rawFileUrl: {
          type: String,
          default: null
        },
        contactLensTypeFilterTitle: {
            type: String,
            default: "Category"
        },
        promotionTiles: {
            type: Array
        }
    },

    data() {
        return {
            // General
            fileUrlPath: window.parsedFileUrl, // need to grab it for later since have issues accessing window scope inside inline-templates
            isFilterSticky: false,
            isLoading: false,
            isMobile: window.innerWidth <= 767,
            mobileSortShow: false,
            mobileFilterShow: false,
            parsedFileUrl: '',

            // Products
            allProducts: [],
            totalResults: null,

            // Pagination
            currentPage: 1,
            isPaginationEnabled: true,
            numberOfProductsToShow: 15,
            pageCount: 0,
            paginateBy: DEFAULT_PAGINATION,
            
            // Sorting
            sortBy: null,
            sortOptions: SHOPIFY_SORT_OPTIONS,

            // Filtering
            filterData: [],
            filterComponentKey: 0, // To force the filter element to re-render
            filteredProducts: [],
            maxPrice: null,
            minPrice: null,
            priceRange: [],
            selectedFilters: this.filterConfig.map(filter => ({ [filter.key]: {} })).reduce((acc, obj) => (acc[Object.keys(obj)[0]] = obj[Object.keys(obj)[0]], acc), {}),
            colourLabels: {},
            desktopFilterShow: false,

            //Algolia
            searchClient: null,
            algoliaConfig: window.algoliaShopify.config,
        }
    },

    created() {
      this.searchClient = algoliasearch(
        this.algoliaConfig.app_id,
        this.algoliaConfig.search_api_key
      );
    },

    computed: {
        productsShowing() {
            // show products based on pagination number (no extra call to Shopify for pagination)
            
            if (this.filteredProducts && this.filteredProducts.length > 0 && this.promotionTiles && this.promotionTiles.length > 0) {
                const productsWithPromotions = this.filteredProducts;
                productsWithPromotions.splice(this.promotionTiles[0].position - 1, 0, this.promotionTiles[0]);
                console.log("this.numberOfProductsToShow", this.numberOfProductsToShow);
                return productsWithPromotions.slice(0, -1);
            }

            return this.filteredProducts.slice(0,this.numberOfProductsToShow);
        },
        filtersSelected() {
          let activeFilters = [];
    
          for (const filter of this.filterData) {
            const checkedFilters = Object.entries(this.selectedFilters[filter.key]).filter(([key, value]) => value == true)
            if (checkedFilters.length) {
              checkedFilters.forEach(chkdfilter => {
                const filterString = `${filter.key}:${chkdfilter[0]}`
                activeFilters.push(filterString)
              })
            }
          }
          return activeFilters.length;
        },
        isMobileMenuOpen() {
            return app.isMenuOpen
        },
        priceFilter() {
          return this.filterData.find(filter => filter.key == PRICE_KEY);
        },
        priceKey() {
            return PRICE_KEY;
        }
    },

    watch: {
        isMobileMenuOpen(value) {
            if (value) {
                this.mobileSortShow = false;
                this.mobileFilterShow = false;
            }
        },

        mobileSortShow(value) {
            this._toggleNoScroll(value);
        },

        mobileFilterShow(value) {
            this._toggleNoScroll(value);
        },

        isLoading (value) {
            const collectionBlocksSection = document.querySelector('.section__collection-blocks')
            const collectionBlocksPlaceholder = document.querySelector('#section__collection-blocks')

            if (!value && collectionBlocksSection) {
                collectionBlocksPlaceholder.appendChild(collectionBlocksSection)
            }
        }
    },

    mounted() {
        this.parsedFileUrl = this.rawFileUrl.replace(/[^\/]+$/,'').substring(0, this.rawFileUrl.lastIndexOf('files'));

        // Set selected filters based on URL params
        const params = new URL(window.location).searchParams;

        // Sort
        if (params.get('sort')) {
            this.sortBy = SHOPIFY_SORT_OPTIONS.find(option => option.shopify_key == params.get('sort'));
        };

        // Set sort v-model
        if (!this.sortBy) {
            const defaultSort = SHOPIFY_SORT_OPTIONS.find(option => option.shopify_key == this.defaultSort);
            this.sortBy = defaultSort ? defaultSort : SHOPIFY_SORT_OPTIONS[0];
        };

        // Pagination
        if (params.get('page')) {
            const page = params.get('page').split('|');
            this.paginateBy = page[0];
            this.currentPage = page[1];
        };

        // Filters
        if (params.get('filter')) {
            let urlFilters = [];
            urlFilters = params.get('filter').split(';');
    
            for (const filter of urlFilters) {

                const split = filter.split('||');
        
                if (split[0].includes('price')) {
                  this.selectedFilters[split[0]] = split[1].split(',')
                } else {
                  Vue.set(this.selectedFilters[split[0]], split[1].split(','), true);
                }
            }
            this.filterData = this.filterConfig
        };
        const PLPRecentVisitedPage = sessionStorage.getItem('PLPRecentVisitedPage') || null
        if (PLPRecentVisitedPage) {
          this.updatePage(PLPRecentVisitedPage, false)
        } else {
          this.getProducts();
        }
        
        // Commenting this code to disable sticky filter in PLP for now.
        // if (!this.isMobile) {

        //     const desktopFiltersOffset = document.querySelector('.collection__filters--desktop').offsetTop;
        //     const headerHeight = document.querySelector('header').offsetHeight;

        //     window.addEventListener(
        //         'scroll', 
        //         debounce(() => {
        //                 let scroll = window.scrollY,
        //                     headerBannerHeight = document.querySelector('.collection__banner').offsetHeight;
        //                     // get banner height on scroll for two reasons: it lazy loads, and the window might have changed width
        //                 this.isFilterSticky = scroll >= (desktopFiltersOffset + headerBannerHeight) - headerHeight;
        //             }, 
        //             150, 
        //             { 
        //                 trailing: true, 
        //                 maxWait: 50
        //             }
        //         )
        //     );
        // }

        // update the type filter title if this is a contact lens collection
        if (!!this.collectionHandle.toLowerCase().includes('contact-lens')) {
            let filter = this.filterConfig.find( filter => filter.key.toLowerCase() === 'tag__type');
            filter.title = this.contactLensTypeFilterTitle;
        }


    },

    methods: {

        // Private
        _getToolbar() {
            return {
                sort_by: this.sortBy.sort_by,
                dir: this.sortBy.dir,
                products_per_page: parseInt(this.paginateBy),
                page: parseInt(this.currentPage)
            }
        },

        getFilters() {
          // See expected request body sample above
    
            let facetFilters = [];
            let numericFilters = []
    
            for (const filter of this.filterData) {
              const checkedFilters = Object.entries(this.selectedFilters[filter.key]).filter(([key, value]) => value == true)
              if (checkedFilters.length) {
                checkedFilters.forEach(chkdfilter => {
                  const filterString = `${filter.key}:${chkdfilter[0]}`
                  facetFilters.push(filterString)
                })
              }
              if (filter.key.includes('price') && Array.isArray(this.selectedFilters[filter.key]) && this.selectedFilters[filter.key].length > 0) {
                numericFilters = [`price>=${this.selectedFilters[filter.key][0]}`, `price<=${this.selectedFilters[filter.key][1]}`]
              }
            }

            const filterParams = {
              facetFilters: Object.values(facetFilters.reduce((acc, curr) => ((acc[curr.split('|')[0]] = acc[curr.split('|')[0]] || []).push(curr), acc), {})),
              numericFilters: numericFilters ? numericFilters : []
            }
            
            return filterParams
        },

        _filter() {
            this.isPaginationEnabled = true;
            let isFiltered = false;
            const url = new URL(window.location.toString());

            for (const k in this.filters) {
                if (this.filters[k].length) {
                    url.searchParams.set(k, encodeURI(this.filters[k]));
                    isFiltered  = true;
                } else {
                    url.searchParams.delete(k);
                }
            }

            if (!isFiltered) {
                this.filteredProducts = this.allProducts;

            } else {
                this.filteredProducts = [];

                this.allProducts.forEach(product => {
                    // if the product contains tags from each chosen filter group, it gets pushed into allProductsFiltered
                    // if the product doesn't contain tags from each filter group, the return will move to next product
                    for (const k in this.filters) {
                        if (this.filters[k].length && !product.tags.some(tag => this.filters[k].includes(tag))) {
                            return;
                        }
                    }
                    this.filteredProducts.push(product);
                });
            }

            // window.history.replaceState({path: url.toString()}, '', url.toString());
            this._updateUrlParams();
        },

        _toggleNoScroll(value) {
            if (value) {
                document.querySelector('body').classList.add('no-scroll');
            } else {
                // delay removing class by animation time, so it doesnt show scroll bar
                // setTimeout(
                    // () => {
                        document.querySelector('body').classList.remove('no-scroll')
                    // }, 225
                // );
            }
        },

        _updateUrlParams() {
            // Set URL params to persist filter selections
      
            // Sort
            if (this.sortBy && this.sortBy.shopify_key != this.defaultSort) {
              updateUrlParameter('sort', this.sortBy.shopify_key);
            } else {
              removeParameterFromUrl('sort');
            }
      
            // Filters
            let filtersArray = [];
            for (const filter of this.filterData) {
              const checkedFilters = Object.entries(this.selectedFilters[filter.key]).filter(([key, value]) => value == true)
              if (checkedFilters.length) {
                checkedFilters.forEach(chkdfilter => {
                  const filterString = `${filter.key}||${chkdfilter[0]}`
                  filtersArray.push(filterString)
                })
              }
              if (filter.key.includes('price') && Array.isArray(this.selectedFilters[filter.key]) && this.selectedFilters[filter.key].length > 0) {
                const filterString = `price_range||${this.selectedFilters[filter.key]}`
                filtersArray.push(filterString)
              }
            }
      
            if (filtersArray.length) {
              updateUrlParameter('filter', filtersArray.join(';'));
            } else {
              removeParameterFromUrl('filter');
            }
        },  

        // Public
        async getProducts() {
          this.isLoading = true;

          const queries = this.getQueries()

          const promotionTilesInCurrentPage = []
          const numberOfProductsInPreviousPage = (queries[0].params.page) * queries[0].params.hitsPerPage
          
          // Check if promo tile/s should be in current page
          for (const promotionTile of this.promotionTiles) {
            if (promotionTile.tilePosition > numberOfProductsInPreviousPage && promotionTile.tilePosition <= ((queries[0].params.page + 1) * queries[0].params.hitsPerPage)) {
              promotionTilesInCurrentPage.push(promotionTile)
            }
          }
          //Adjust products per page to offset number of promo tiles
          queries[0].params.hitsPerPage -= (promotionTilesInCurrentPage.length || 0)

          try {
            this.searchClient
            .search(queries)
            .then(async ({ results }) => {
              const productHits = results[0].hits
              this.totalResults = results[0].nbHits
              const productDetails = await getProductDetails(productHits.map(product => `"${product.handle}"`))
              this.products = productHits.map((product, index) => {
                product.images = productDetails.filter(prod => prod ? prod.id == product.id : null)[0]?.images || [product.image]
                product.variants = productDetails.filter(prod => prod ? prod.id == product.id : null)[0]?.variants || []
                product.compare_at_price = product.compare_at_price * 100
                product.compare_at_price_min = product.variants_compare_at_price_min * 100
                product.compare_at_price_max = product.variants_compare_at_price_max * 100
                product.price = product.price * 100
                product.price_min = product.variants_min_price * 100
                product.price_max = product.variants_min_price * 100

                return product
              })

              if (promotionTilesInCurrentPage.length) {
                promotionTilesInCurrentPage.sort((a, b) => a.tilePosition - b.tilePosition)
                for (const promotionTile of promotionTilesInCurrentPage) {
                  // Insert promo tile in to products array
                  this.products.splice((promotionTile.tilePosition - 1 - numberOfProductsInPreviousPage), 0, promotionTile)
                }
              }

              this.productCount = results[0].nbHits;
              this.pageCount = results[0].nbPages;

              const facetsFromAlgolia = results.reduce((acc, obj) => Object.assign(acc, obj.facets), {});
              
              if (facetsFromAlgolia) {
                const facets = this.filterConfig.map(filter => {
                  if (facetsFromAlgolia[filter.key]) {
                    filter.options = Object.keys(facetsFromAlgolia[filter.key])
                    return filter
                  } else {
                    filter.options = []
                    return filter;
                  }
                }).filter(Boolean)

                // Handle tag facets
                const tagsFacet = facets.find(facet => facet.key == 'tags')
                const tagOptions = tagsFacet.options.reduce((obj, curr) => {
                  const [key, value] = curr.split("|")

                  if (!value || !this.filterConfig.map(filter => filter.key).includes(key)) return obj

                  if (!obj[key]) return {...obj, [key]: [value]}
                  
                  return {...obj, [key]: [...obj[key], value].sort()}
                }, {})

                const facetsWithTags = facets.map(facet => {
                  if (!!tagOptions[facet.key]) return {...facet, options: tagOptions[facet.key], tagFacet: true}

                  return facet;
                })

                // Sort vendors
                const vendorFacet = facetsWithTags.find(facet => facet.key == 'vendor')
                const vendorFacetIndex = facetsWithTags.indexOf(vendorFacet)
                const sortedVendorFacet = {...vendorFacet, title: "Brand", options: vendorFacet.options.sort()}
                facetsWithTags[vendorFacetIndex] = sortedVendorFacet

                this.filterData = [...facetsWithTags].filter(facet => facet.title != 'Vendor');

                // Handle Price Range Facet
                const priceRangeFilter = this.priceFilter
                const priceRangeFilterIndex = this.filterData.indexOf(priceRangeFilter)
                priceRangeFilter.options = {
                  min: results[0].facets_stats.price.min,
                  max: results[0].facets_stats.price.max
                }
                this.filterData[priceRangeFilterIndex] = priceRangeFilter
              }

              const PLPLastScroll = sessionStorage.getItem('PLPLastScroll') || 0

              this._updateUrlParams();
              await this._getSiblings()
              this.allProducts = this.products;
              this._getTryOnData()
              this.initPriceSliderValue()
              this._filter()
              window.scrollTo(0, PLPLastScroll);
              this.isLoading = false
            })
          } catch (error) {
            this.isLoading = false
            console.error(error)
          }
        },

        async _getSiblings() {
          const productInfo = this.products.map(product => {
            const modelTag = product.tags.find(tag => tag.includes('model'))
            return {
              id: product.id,
              modelTag
            }
          })
          const consolidatedSiblings = await getSiblingsGraphQL(productInfo)
          const updatedProducts = this.products.map(product => {
            const productSiblings = consolidatedSiblings.find(siblingProduct => siblingProduct.id == product.id)
            if (productSiblings) {
              product.siblings = productSiblings.siblings.map(sibling => (
                {
                  url: `/products/${sibling?.handle}`,
                  title: sibling?.title,
                  tags: sibling?.tags,
                  handle: sibling?.handle
                }
              ))
            }
            return product
          })
          this.products = updatedProducts.map( product => {
            let siblingsData = null;

            if (!!product.siblings && !!product.siblings.length) {
                siblingsData = product.siblings.map( sibling => {

                    let modelTag = sibling.tags.find( tag => tag.toLowerCase().split('|')[0] === 'model'),
                        modelTagData = modelTag ? (
                            modelTag.split('|')[1].replaceAll('_', ' ').toLowerCase()
                        ) : '',
                        
                        colourDescriptionTag = sibling.tags.find( tag => tag.toLowerCase().split('|')[0] === 'colour_description'),
                        colourDescriptionTagData = colourDescriptionTag ? (
                            colourDescriptionTag.split('|')[1].replaceAll('_', ' ').toLowerCase()
                        ) : '',

                        colourFamilyTag = (sibling.tags.find( tag => tag.toLowerCase().split('|')[0] === 'colour_family') || colourDescriptionTag),
                        colourFamilyTagData = colourFamilyTag ? (
                            colourFamilyTag.split('|')[1].replaceAll(' ', '-').toLowerCase()
                        ) : '',
                        output = {
                            ...sibling,
                            'title': `${modelTagData} ${colourDescriptionTagData}`,
                            'swatchUrl': this.parsedFileUrl + "files/colour-swatch-" + colourFamilyTagData + ".jpg",
                            'url': `/products/${sibling['handle']}`
                        };

                    return !!colourFamilyTagData ? output : false;

                })
                .filter(sibling => !!sibling);
            }

            return {
                ...product,
                'siblings': siblingsData
            }
        })
        },

        getQueries() {
          const filters = []
          const defaultQuery = {
            indexName: getAlgoliaIndex(this.sortBy, this.algoliaConfig.index_prefix),
            params: {
              distinct: true,
              filters: `collections:${this.collectionHandle} AND inventory_quantity > 0`,
              facets: [],
              ...this.getFilters(),
              hitsPerPage: this.paginateBy,
              page: this.currentPage - 1,
            },
          }
          const query = cloneDeep(defaultQuery)
          if (this.filterConfig) {
            query.params.facets = [...this.filterConfig?.map(filter => filter.key), 'price']
          }
          filters.push(query)

          const emptyQuery = cloneDeep(defaultQuery)
          emptyQuery.params.facets = []
          emptyQuery.params.facetFilters = []
          emptyQuery.params.numericFilters = []
          emptyQuery.params.hitsPerPage = 0
          emptyQuery.params.page = 0
          filters.push(emptyQuery)

          return filters
        },

        priceUpdated: debounce(function() {
            this.$nextTick( () => {
                if (this.priceRange[1] != this.maxPrice || this.priceRange[0] != this.minPrice) {
                    this.selectedFilters[PRICE_KEY] = this.priceRange;
                } else {
                    this.selectedFilters[PRICE_KEY] = [];
                }
                this.getProducts();
            });
        }, 500),

        updatePage(page, fromPaginate = true) {
            this.currentPage = page;
            if (fromPaginate) {
              sessionStorage.removeItem('PLPLastScroll')
            }
            sessionStorage.setItem('PLPRecentVisitedPage', page)
            this.getProducts();
        },

        clearFilters(value) {
            if (value === 'all') {
                for (let filter in this.selectedFilters) {
                    Vue.set(this.selectedFilters, filter, []);
                }
            } else {
                Vue.set(this.selectedFilters, value, []);
            }
            this.getProducts();
        },

        toggleMobileSort() {
            this.mobileSortShow = !this.mobileSortShow;
            app.isMenuOpen = false;
        },

        toggleMobileFilter() {
            this.mobileFilterShow = !this.mobileFilterShow;
            app.isMenuOpen = false;
        },

        toggleDesktopFilter () {
            this.desktopFilterShow = !this.desktopFilterShow
        },

        ///////////////////
        async _getTryOnData() {
            if (!!this.allProducts) {
                let temp = this.allProducts.map( product => {
                    let vtoTag = product.tags.find(tag => tag.includes('fittingbox_vto')),
                        vtoValue = !!vtoTag ? (vtoTag.split('|')[1].toLowerCase() ===  'true') : false,
                        glassesIdTag = product.tags.find(tag => tag.includes('fittingbox_glassesid')),
                        glassesIdValue = !!glassesIdTag ? glassesIdTag.split('|')[1].toLowerCase() : null;

                    return {
                        ...product,
                        hasTryOn: vtoValue,
                        glassesId: glassesIdValue
                    }
                });

                this.$nextTick( async () => {
                    this.allProducts = temp;
                    await this._initTryOn();
                    this._filter();
                    this.filterComponentKey += 1;
                    this.isLoading = false;
                });
            } else {
                this._filter();
                this.filterComponentKey += 1;
                this.isLoading = false;
            }
        },
        initPriceSliderValue() {
            if (!!this.priceFilter && !!this.priceFilter.options) {
              /* 
                  When there is no min or max price, the Math filter returns a NaN since its trying to format 
                  an Undefined as an Int. When the min and max price are NaN, the vue-slider breaks really badly, 
                  but we can use this to our advantage.
                  
                  By evaluating the min and max prices as a Bool, we can then see if they are NaN and, if so, 
                  make priceRange an empty Array. The Vue-Slider only renders when priceRange has a length > 0, so we
                  avoid it breaking by not allowing it to render at all.
              */
            
              this.minPrice = !!this.priceFilter.options.min ? Math.floor(parseFloat(this.priceFilter.options.min)) : null;
              this.maxPrice = !!this.priceFilter.options.max ?Math.ceil(parseFloat(this.priceFilter.options.max)) : null;

              let boolMin = !!this.minPrice;
                  // boolMax = !!this.maxPrice;

              let isValidPriceRange = 
                  // if max and min are the same, then dont bother showing the filter
                  this.minPrice === this.maxPrice ? false :
                  (
                      // if boolMin is TRUE then it is a valid number greater than 0 so all good
                      !!boolMin ? true : 
                      (
                          // otherwise if boolMin is FALSE we need to check if it isn't just a 0, because that is a valid price for the filter
                          this.minPrice !== 0 ? true : false
                      )
                  );

              if (isValidPriceRange) {
              
                  if (!!this.selectedFilters[PRICE_KEY].length) {
                      this.priceRange = [
                          Math.floor(parseFloat(this.selectedFilters[PRICE_KEY][0])), 
                          Math.ceil(parseFloat(this.selectedFilters[PRICE_KEY][1]))
                      ];
                  } else {
                      this.priceRange = [
                          this.minPrice,
                          this.maxPrice
                      ]
                  };
              } else {
                  this.priceRange = [];
              }

          } else {
              this.priceRange = [];
          }
        },

    }
}